<template>
    <asli-form @submitted="goNext">
        <asli-form-section title="Your Property" heading-level="h2">
            <asli-dropdown
                id="cover_type"
                name="cover_type"
                rules="required:cover_type"
                v-model="residencyDetails.residence_ownership_status"
                label="Who owns and lives at this property?"
                :options="lists.residence_ownership_status"
                placeholder="Please select an option"
                large
            />
            <form-transition :become-visible-when="showHolidayHomeQuestion" @show="commitRisk">
                <asli-radio
                    v-if="showHolidayHomeQuestion"
                    id="rents_out_holiday_home"
                    name="rents_out_holiday_home"
                    v-model="residencyDetails.residence_holiday_home_type"
                    label="Do you rent or let out your holiday home?"
                    :options="lists.residence_holiday_home_type"
                    rules="required:residence_holiday_home_type"
                    is-yes-no-option
                />
            </form-transition>
            <form-transition :become-visible-when="showHowManyIndividualsQuestion" @show="commitRisk">
                <asli-radio
                    v-if="showHowManyIndividualsQuestion"
                    id="residence_no_of_tenants"
                    name="residence_no_of_tenants"
                    v-model="residencyDetails.residence_no_of_tenants"
                    label="How many people live at this property?"
                    :options="lists.residence_no_of_tenants"
                    rules="required:residence_no_of_tenants"
                    :has-fixed-width-for-number-options="true"
                />
            </form-transition>
            <form-transition :become-visible-when="showPayingGuestQuestion" @show="commitRisk">
                <asli-radio
                    v-if="showPayingGuestQuestion"
                    id="number_of_paying_guests"
                    name="number_of_paying_guests"
                    v-model="residencyDetails.number_of_paying_guests"
                    label="How many paying guests are staying in the property?"
                    :options="payingGuestsOptions"
                    rules="required"
                    :has-fixed-width-for-number-options="true"
                />
            </form-transition>
            <form-transition :become-visible-when="moveOnFromResidencyQuestions" @show="commitRisk">
                <asli-dropdown
                    id="property_type"
                    name="property_type"
                    rules="required:property_type"
                    large
                    v-model="propertyDetails.type"
                    label="What type of property do you have?"
                    :options="lists.property_type"
                    placeholder="Please select an option"
                />
            </form-transition>
            <form-transition :become-visible-when="propertyDetails.type !== null" @show="commitRisk">
                <asli-search
                    @option-selected="updateYearBuilt"
                    id="year_built"
                    name="year_built"
                    large
                    v-model="yearCurrentValue"
                    label="What year was the property built?"
                    :options="yearBuiltOptions"
                    :rules="{ required: 'year_built', 'in-list-year-built': yearBuiltOptions }"
                    mask="####"
                    placeholder="Please select an option"
                    show-dropdown-on-focus
                />
            </form-transition>
            <form-transition :become-visible-when="propertyDetails.year_built !== null" @show="commitRisk">
                <asli-radio
                    id="number_of_bedrooms"
                    name="number_of_bedrooms"
                    v-model="propertyDetails.number_of_bedrooms"
                    label="How many bedrooms are in the property?"
                    :options="roomOptions"
                    rules="required:number_of_bedrooms"
                    :has-fixed-width-for-number-options="true"
                />
            </form-transition>
            <form-transition :become-visible-when="propertyDetails.number_of_bedrooms !== null" @show="commitRisk">
                <asli-radio
                    id="number_of_bathrooms"
                    name="number_of_bathrooms"
                    v-model="propertyDetails.number_of_bathrooms"
                    label="How many bathrooms are in the property?"
                    :options="roomOptions"
                    :tooltipText="YOUR_PROPERTY_HELP_TEXT.number_of_bathrooms"
                    rules="required:number_of_bathrooms"
                    :has-fixed-width-for-number-options="true"
                />
            </form-transition>
            <form-transition :become-visible-when="propertyDetails.number_of_bathrooms !== null" @show="commitRisk">
                <template v-if="propertyDetails.security_details">
                    <asli-radio
                        id="alarm_type"
                        name="alarm_type"
                        rules="required:alarm_type"
                        v-model="propertyDetails.security_details.alarm_type"
                        label="Is your property protected by an alarm to the approved standard?"
                        :options="alarmOptions"
                        :placeholder="`Please Select...`"
                        large
                        :tooltipText="YOUR_PROPERTY_HELP_TEXT.alarm_type"
                    />
                </template>
            </form-transition>
            <form-transition
                :become-visible-when="propertyDetails.security_details?.alarm_type !== null"
                @show="commitRisk"
            >
                <asli-radio
                    id="is_occupied_during_day"
                    name="is_occupied_during_day"
                    v-model="residencyDetails.is_occupied_during_day"
                    label="Is the property usually occupied during the day?"
                    :tooltipText="YOUR_PROPERTY_HELP_TEXT.is_occupied_during_day"
                    :options="lists.yes_no"
                    :is-yes-no-option="true"
                    rules="required:is_occupied_during_day"
                    return-boolean
                />
            </form-transition>
            <form-transition :become-visible-when="residencyDetails.is_occupied_during_day !== null" @show="commitRisk">
                <asli-dropdown
                    id="heating_type"
                    name="heating_type"
                    rules="required:heating_type"
                    large
                    v-model="propertyDetails.heating_system"
                    label="What is the main source of heating in the property?"
                    :options="lists.heating_type"
                    placeholder="Please select an option"
                />
            </form-transition>
            <form-transition :become-visible-when="propertyDetails.heating_system !== null" @show="commitRisk">
                <asli-dropdown
                    id="building_energy_rating"
                    name="building_energy_rating"
                    rules="required:building_energy_rating"
                    large
                    v-model="propertyDetails.building_energy_rating"
                    label="What is the Building Energy Rating (BER) for your property?"
                    :options="lists.building_energy_rating"
                    placeholder="Please select an option"
                    :tooltipText="YOUR_PROPERTY_HELP_TEXT.building_energy_rating"
                />
            </form-transition>
        </asli-form-section>

        <form-transition :become-visible-when="propertyDetails.building_energy_rating !== null" @show="commitRisk">
            <asli-form-section title="Your Address" class-name="mb-[4rem] sm:mb-[2rem]" heading-level="h2">
                <asli-auto-address-search
                    id="address"
                    name="address"
                    v-model="address"
                    label="What is the Address or Eircode of the property you wish to insure?"
                    description="Type your Eircode or address and select an option from the list provided."
                    :options="addressOptions"
                    placeholder="Start typing your Eircode or address"
                    :rules="{ 'required-address': true }"
                    :address-meta="addressMeta"
                    :tooltipText="YOUR_PROPERTY_HELP_TEXT.address"
                    @update:model-value="onAddressUpdate"
                    @has-missing-eircode="handleRiskAddressMissingEircode"
                />
                <form-transition
                    :become-visible-when="
                        !!address?.address_line1 &&
                        !!address?.eircode &&
                        showCorrespondenceAddressQuestion &&
                        !riskAddressMissingEircode
                    "
                    @show="commitRisk"
                >
                    <asli-radio
                        v-if="
                            !!address?.address_line1 &&
                            !!address?.eircode &&
                            showCorrespondenceAddressQuestion &&
                            !riskAddressMissingEircode
                        "
                        id="has_different_correspondence_address"
                        name="has_different_correspondence_address"
                        v-model="propertyDetails.has_different_correspondence_address"
                        label="Is this address the same as your postal address?"
                        :options="reversedYesNoList"
                        :is-yes-no-option="true"
                        rules="required:has_different_correspondence_address"
                        return-boolean
                    />
                </form-transition>
                <form-transition
                    :become-visible-when="
                        propertyDetails.has_different_correspondence_address === true &&
                        !!address?.address_line1 &&
                        !!address?.eircode &&
                        showCorrespondenceAddressQuestion &&
                        !riskAddressMissingEircode
                    "
                    @show="commitRisk"
                >
                    <asli-auto-address-search
                        v-if="
                            propertyDetails.has_different_correspondence_address &&
                            !!address?.address_line1 &&
                            !!address?.eircode &&
                            showCorrespondenceAddressQuestion &&
                            !riskAddressMissingEircode
                        "
                        id="correspondence_address"
                        name="correspondence_address"
                        :rules="{ 'required-address': true }"
                        label="What is your postal address/Eircode?"
                        description="Type your Eircode or address and select an option from the list provided."
                        :options="correspondenceAddressOptions"
                        v-model="correspondenceAddress"
                        placeholder="Start typing your Eircode or address"
                        :is-primary="false"
                        :address-meta="addressMeta"
                        hide-property-stats
                        @has-missing-eircode="handleCorrespondenceAddressMissingEircode"
                    />
                </form-transition>
            </asli-form-section>
        </form-transition>

        <form-buttons @back="goBack" :show-continue="formIsEntirelyVisible" :is-disabled="isMissingEircode" />
    </asli-form>
</template>

<script lang="ts" setup>
import { useRouter } from "vue-router";
import {
    AsliFormSection,
    AsliForm,
    AsliRadio,
    AsliDropdown,
    AsliSearch,
    AsliAutoAddressSearch,
    getHeatingSystemDisplay
} from "@mbs-npm/anpost-ui";
import FormButtons from "@/components/FormButtons.vue";
import { ROUTE_NAMES } from "@/router/types";
import { useMainStore } from "@/stores/main";
import { getResolvedRouteName } from "@/router/utils";
import { useMainPersistentStore } from "@/stores/mainPersistent";
import { useProposalStore } from "@/stores/risk/risk";
import { useListStore } from "@/stores/lists/lists";
import { payingGuestsOptions, roomOptions, alarmOptions, MAX_YEAR, MIN_YEAR } from "@/static/constants";
import { reactive, computed, ref, onMounted, watch } from "vue";
import { PropertyDetails, ResidencyDetails } from "@/stores/risk/types/types";
import FormTransition from "@/components/FormTransitions/FormTransition.vue";
import { YOUR_PROPERTY_HELP_TEXT } from "@/utils/helpText";
import { Address, AddressMeta } from "@/stores/risk/types/drivers/driver-types";
import dayjs from "dayjs";
import { RentalStatus, ResidencyOptions, ResidencyOwnershipStatus, Option } from "@/stores/lists/types";
import { getListValueByName } from "@/hooks/getListValueName";
import { getIcon } from "@/hooks/getIcon";
import { APP_STEP } from "@/stores/types";

const mainStore = useMainStore();
const mainStorePersistent = useMainPersistentStore();
const proposalStore = useProposalStore();
const listStore = useListStore();
const router = useRouter();

const lists = computed(() => listStore?.lists);
const storedRisk = proposalStore.proposal.risk;

mainStore.setAppStep(APP_STEP.YOUR_PROPERTY);

const riskAddressMissingEircode = ref(false);
const correspondenceAddressMissingEircode = ref(false);

const isMissingEircode = computed(() => {
    const isPrimaryMissingEircode = address.value && riskAddressMissingEircode.value;
    const isCorrespondenceMissingEircode = correspondenceAddress.value && correspondenceAddressMissingEircode.value;

    const result = isPrimaryMissingEircode || isCorrespondenceMissingEircode;
    return result || false;
});

function handleRiskAddressMissingEircode(isMissing: boolean) {
    riskAddressMissingEircode.value = isMissing;

    if (isMissing && propertyDetails.has_different_correspondence_address === true) {
        propertyDetails.has_different_correspondence_address = null;
        correspondenceAddress.value = null;
    }
}

function handleCorrespondenceAddressMissingEircode(isMissing: boolean) {
    correspondenceAddressMissingEircode.value = isMissing;
}

const yearBuiltOptions = computed(() => {
    let years: Array<Option> = [];
    for (let i = MAX_YEAR; i >= MIN_YEAR; i--) {
        years.push({
            name: i.toString(),
            value: i.toString()
        });
    }

    return years;
});

const addressOptions = ref([]);
const correspondenceAddressOptions = ref([]);
const correspondenceAddressInit = computed(() => {
    return {
        address_line1: storedRisk.proposer.address.address_line1,
        address_line2: storedRisk.proposer.address.address_line2,
        address_line3: storedRisk.proposer.address.address_line3,
        address_line4: storedRisk.proposer.address.address_line4,
        ecad_id: storedRisk.proposer.address.ecad_id,
        eircode: storedRisk.proposer.address.eircode
    };
});

const addressInit = computed(() => {
    return {
        address_line1: storedRisk.property_details.address.address_line1,
        address_line2: storedRisk.property_details.address.address_line2,
        address_line3: storedRisk.property_details.address.address_line3,
        address_line4: storedRisk.property_details.address.address_line4,
        ecad_id: storedRisk.property_details.address.ecad_id,
        eircode: storedRisk.property_details.address.eircode
    };
});

const address = ref<Partial<Address> | null>(addressInit.value.address_line1 ? { ...addressInit.value } : null);
const correspondenceAddress = ref<Partial<Address> | null>(
    correspondenceAddressInit.value.address_line1 ? { ...correspondenceAddressInit.value } : null
);

const propertyDetails = reactive<Partial<PropertyDetails>>({
    type: storedRisk.property_details.type,
    year_built: storedRisk.property_details.year_built,
    number_of_bathrooms: storedRisk.property_details.number_of_bathrooms,
    number_of_bedrooms: storedRisk.property_details.number_of_bedrooms,
    heating_system: storedRisk.property_details.heating_system,
    security_details: {
        alarm_type: storedRisk.property_details.security_details.alarm_type ?? null,
        has_locks_on_all_doors_windows: storedRisk.property_details.security_details.has_locks_on_all_doors_windows
    },
    has_different_correspondence_address: storedRisk.property_details.has_different_correspondence_address,
    building_energy_rating: storedRisk.property_details.building_energy_rating
});

const residencyDetails = reactive<ResidencyDetails>({
    residence_ownership_status: storedRisk.residency_details.residence_ownership_status ?? null,
    residence_holiday_home_type: storedRisk.residency_details.residence_holiday_home_type ?? null,
    residence_no_of_tenants: storedRisk.residency_details.residence_no_of_tenants ?? null,
    is_occupied_during_day: storedRisk.residency_details.is_occupied_during_day ?? null,
    is_landlord: storedRisk.residency_details.is_landlord ?? null,
    number_of_paying_guests: storedRisk.residency_details.number_of_paying_guests ?? null,
    residence_rental_status: storedRisk.residency_details.residence_rental_status ?? null
});

const addressMeta = ref<AddressMeta>({
    icon: getIcon(propertyDetails.type),
    type: getListValueByName("property_type", propertyDetails.type) ?? "",
    year_built: propertyDetails.year_built?.split("-")[0] ?? "",
    number_of_bathrooms:
        propertyDetails.number_of_bathrooms === 0 || propertyDetails.number_of_bathrooms
            ? parseInt(propertyDetails.number_of_bathrooms.toString() ?? "")
            : "",
    number_of_bedrooms:
        propertyDetails.number_of_bedrooms === 0 || propertyDetails.number_of_bedrooms
            ? parseInt(propertyDetails.number_of_bedrooms.toString() ?? "")
            : "",
    heating_system: getHeatingSystemDisplay(propertyDetails?.heating_system ?? "")
});

function buildYearForBackEnd(year: string | null) {
    return year ? `${year}-01-01 00:00:00` : null;
}

const yearCurrentValue = ref(
    (() => {
        const yearBuilt = propertyDetails.year_built ? dayjs(propertyDetails.year_built) : null;
        return yearBuilt && yearBuilt.isValid() ? yearBuilt.year().toString() : null;
    })()
);

const updateYearBuilt = () => {
    propertyDetails.year_built = buildYearForBackEnd(yearCurrentValue.value);
};

watch(
    () => address.value,
    newAddress => {
        if (!newAddress) {
            riskAddressMissingEircode.value = false;
            correspondenceAddressMissingEircode.value = false;
            correspondenceAddress.value = null;
            propertyDetails.has_different_correspondence_address = null;
        } else if (!newAddress.eircode) {
            correspondenceAddress.value = null;
            propertyDetails.has_different_correspondence_address = null;
        }
    }
);

watch(
    () => correspondenceAddress.value,
    (newValue, oldValue) => {
        if (oldValue && !newValue) {
            correspondenceAddressMissingEircode.value = false;
        }
    },
    { deep: true }
);

watch(
    () => propertyDetails.has_different_correspondence_address,
    newValue => {
        correspondenceAddress.value = newValue ? {} : address.value;
    },
    { deep: true }
);

watch(propertyDetails, newValue => {
    if (propertyDetails.type) {
        addressMeta.value.icon = getIcon(newValue.type);
        addressMeta.value.type = getListValueByName("property_type", newValue.type) ?? "";
    }
    if (propertyDetails.year_built) addressMeta.value.year_built = newValue.year_built?.split("-")[0] ?? "";
    if (propertyDetails.number_of_bathrooms || propertyDetails.number_of_bathrooms === 0)
        addressMeta.value.number_of_bathrooms = newValue.number_of_bathrooms ?? "";
    if (propertyDetails.number_of_bedrooms || propertyDetails.number_of_bedrooms === 0)
        addressMeta.value.number_of_bedrooms = newValue.number_of_bedrooms ?? "";
    if (propertyDetails.heating_system)
        addressMeta.value.heating_system = getHeatingSystemDisplay(newValue?.heating_system ?? "");
});

watch(
    () => residencyDetails.residence_ownership_status,
    (_newValue, oldValue) => {
        if (oldValue === ResidencyOwnershipStatus.IsLandlord) {
            correspondenceAddress.value = {};
            propertyDetails.has_different_correspondence_address = null;
        }
    },
    { deep: true }
);

onMounted(() => {
    if (residencyDetails.residence_ownership_status) {
        const rentalOptions: Array<ResidencyOptions> = [
            ResidencyOptions.IsLandlord,
            ResidencyOptions.IsNotLandlord,
            ResidencyOptions.RentedFamily,
            ResidencyOptions.RentedCouple,
            ResidencyOptions.RentedStudents,
            ResidencyOptions.IndividualTenants
        ];
        const numberOptions: Array<ResidencyOptions> = [
            ResidencyOptions.RentedOnePerson,
            ResidencyOptions.RentedTwoPeople,
            ResidencyOptions.RentedThreePeople,
            ResidencyOptions.RentedFourPeople,
            ResidencyOptions.RentedFivePeople,
            ResidencyOptions.RentedSixPeople,
            ResidencyOptions.RentedSevenPeople,
            ResidencyOptions.RentedEightPeople,
            ResidencyOptions.RentedNinePeople,
            ResidencyOptions.RentedTenPeople
        ];
        const isRented = [...rentalOptions, ...numberOptions].includes(residencyDetails.residence_ownership_status);

        const holidayHomeOptions: Array<ResidencyOptions> = [
            ResidencyOptions.HolidayHomeFamilyUse,
            ResidencyOptions.HolidayHomeLetOut
        ];

        const isHolidayHome = holidayHomeOptions.includes(residencyDetails.residence_ownership_status);

        if (isRented) {
            switch (residencyDetails.residence_ownership_status) {
                case ResidencyOptions.RentedFamily:
                    residencyDetails.residence_rental_status = RentalStatus.RentedFamily;
                    break;

                case ResidencyOptions.RentedCouple:
                    residencyDetails.residence_rental_status = RentalStatus.RentedCouple;
                    break;

                case ResidencyOptions.RentedStudents:
                    residencyDetails.residence_rental_status = RentalStatus.RentedStudents;
                    break;

                case ResidencyOptions.IndividualTenants:
                    residencyDetails.residence_rental_status = RentalStatus.IndividualTenants;
                    break;
            }

            if (numberOptions.includes(residencyDetails.residence_ownership_status)) {
                residencyDetails.residence_rental_status = RentalStatus.IndividualTenants;
            }

            if (residencyDetails.is_landlord) {
                residencyDetails.residence_ownership_status = ResidencyOptions.IsLandlord;
            } else if (residencyDetails.is_landlord === false) {
                residencyDetails.residence_ownership_status = ResidencyOptions.IsNotLandlord;
            }
        } else if (isHolidayHome) {
            residencyDetails.residence_ownership_status = ResidencyOptions.IsHolidayHome;
        }
    }

    if (address.value) {
        handleRiskAddressMissingEircode(!address.value.eircode);
    }

    if (correspondenceAddress.value) {
        handleCorrespondenceAddressMissingEircode(!correspondenceAddress.value.eircode);
    }
});

const reversedYesNoList = computed(() => {
    const newList = lists.value.yes_no.map(item => {
        return {
            ...item,
            value: item.value === "true" ? "false" : "true"
        };
    });
    return newList;
});

const showCorrespondenceAddressQuestion = computed(() => {
    return (
        residencyDetails.residence_ownership_status === ResidencyOptions.IsLandlord ||
        residencyDetails.residence_ownership_status === ResidencyOptions.IsHolidayHome
    );
});

const showPayingGuestQuestion = computed(() => {
    return (
        (residencyDetails.residence_rental_status !== null &&
            residencyDetails.residence_ownership_status === ResidencyOptions.IsNotLandlord &&
            residencyDetails.residence_rental_status !== RentalStatus.IndividualTenants) ||
        (residencyDetails.residence_rental_status !== null &&
            residencyDetails.residence_ownership_status !== ResidencyOptions.IsLandlord &&
            residencyDetails.residence_rental_status === RentalStatus.IndividualTenants &&
            residencyDetails.residence_no_of_tenants !== null)
    );
});

const showHowManyIndividualsQuestion = computed(() => {
    return (
        residencyDetails.residence_ownership_status === ResidencyOptions.IsLandlord ||
        residencyDetails.residence_ownership_status === ResidencyOptions.IsNotLandlord
    );
});

const showHolidayHomeQuestion = computed(() => {
    return residencyDetails.residence_ownership_status === ResidencyOptions.IsHolidayHome;
});

const moveOnFromResidencyQuestions = computed(() => {
    switch (residencyDetails.residence_ownership_status) {
        case ResidencyOptions.UnderRenovation:
        case ResidencyOptions.Unoccupied:
        case ResidencyOptions.OwnerOccupied:
            return true;
        case ResidencyOptions.IsLandlord:
        case ResidencyOptions.IsNotLandlord:
            return residencyDetails.residence_no_of_tenants !== null;
        case ResidencyOptions.IsHolidayHome:
            return residencyDetails.residence_holiday_home_type !== null;
    }

    return false;
});

const onAddressUpdate = () => {
    propertyDetails.has_different_correspondence_address = null;

    if (!address.value) {
        riskAddressMissingEircode.value = false;
        correspondenceAddressMissingEircode.value = false;
        correspondenceAddress.value = null;
    } else {
        const hasNoEircode = !address.value.eircode;
        riskAddressMissingEircode.value = hasNoEircode;
        if (hasNoEircode) {
            correspondenceAddress.value = null;
        }
    }

    if (!showCorrespondenceAddressQuestion.value && address.value && address.value.eircode) {
        correspondenceAddress.value = { ...address.value };
    } else if (!address.value || !address.value.eircode) {
        correspondenceAddress.value = null;
    }
};

function cleanUpData(data: ResidencyDetails) {
    const updatedResidencyDetails = {
        ...data,
        residence_ownership_status: residencyDetails.residence_ownership_status
    };
    let result = { ...updatedResidencyDetails };
    const isRented: boolean =
        updatedResidencyDetails.residence_ownership_status === ResidencyOwnershipStatus.IsNotLandlord;
    const isLandlord: boolean =
        updatedResidencyDetails.residence_ownership_status === ResidencyOwnershipStatus.IsLandlord;
    const isUnoccupied: boolean =
        updatedResidencyDetails.residence_ownership_status === ResidencyOwnershipStatus.Unoccupied;
    const isUnderRenovation: boolean =
        updatedResidencyDetails.residence_ownership_status === ResidencyOwnershipStatus.UnderRenovation;
    const isOwnerOccupied: boolean =
        updatedResidencyDetails.residence_ownership_status === ResidencyOwnershipStatus.OwnerOccupied;
    const isHolidayHome = showHolidayHomeQuestion.value;

    if (!isOwnerOccupied && !isRented) {
        proposalStore.patchCoverDetails({
            ...storedRisk.cover_details,
            has_specified_items: null,
            specified_items: [],
            unspecified_risk_amount: 0
        });
    }

    if (isOwnerOccupied || isUnderRenovation) {
        result = {
            ...result,
            is_landlord: null,
            residence_holiday_home_type: null,
            residence_no_of_tenants: null,
            residence_rental_status: null
        };
    } else if (isUnoccupied) {
        result = {
            ...result,
            is_landlord: null,
            number_of_paying_guests: null,
            residence_holiday_home_type: null,
            residence_no_of_tenants: null,
            residence_rental_status: null
        };
    } else if (isRented) {
        result = {
            ...result,
            is_landlord: isLandlord ? true : false,
            residence_holiday_home_type: null,
            residence_no_of_tenants: updatedResidencyDetails.residence_no_of_tenants
        };
    } else if (isHolidayHome) {
        result = {
            ...result,
            is_landlord: null,
            number_of_paying_guests: null,
            residence_rental_status: null,
            residence_no_of_tenants: null
        };
    }

    Object.assign(residencyDetails, result);
    proposalStore.patchResidencyDetails({
        ...storedRisk.residency_details,
        ...residencyDetails
    });
}

async function commitRisk() {
    const correspondenceAddressObj = correspondenceAddress.value ? { ...correspondenceAddress.value } : {};
    const addressObj = address.value ? { ...address.value } : {};

    const updatedProposer = {
        ...storedRisk.proposer,
        address: {
            ...storedRisk.proposer.address,
            ...correspondenceAddressObj
        }
    };

    const updatedPropertyDetails = {
        ...storedRisk.property_details,
        ...propertyDetails,
        address: {
            ...storedRisk.property_details.address,
            ...addressObj
        },
        year_built:
            propertyDetails.year_built?.length === 4
                ? buildYearForBackEnd(propertyDetails.year_built)
                : propertyDetails.year_built
    };

    const updatedResidencyDetails = {
        ...storedRisk.residency_details,
        ...residencyDetails
    };

    proposalStore.patchProposer(updatedProposer);
    proposalStore.patchPropertyDetails(updatedPropertyDetails);
    proposalStore.patchResidencyDetails(updatedResidencyDetails);
}

function goBack() {
    commitRisk();
    router.push({
        name: getResolvedRouteName(ROUTE_NAMES.AboutYou, router.currentRoute.value),
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}

async function goNext() {
    await commitRisk();
    if (!showCorrespondenceAddressQuestion.value) {
        const addressObj = address.value ? { ...address.value } : {};

        proposalStore.patchProposer({
            ...storedRisk.proposer,
            address: {
                ...storedRisk.proposer.address,
                ...addressObj
            }
        });
    }
    cleanUpData(residencyDetails);
    mainStorePersistent.setGuardStep(APP_STEP.YOUR_COVER);
    await router.push({ name: getResolvedRouteName(ROUTE_NAMES.YourCover, router.currentRoute.value) });
}

const formIsEntirelyVisible = computed(() => residencyDetails.is_occupied_during_day !== null);
</script>
